"use client";

/**
 * Installed-PWA auto-update detector (mounted once at the layout root).
 *
 * Problem: there is no service worker (by design), so an installed PWA (iPhone
 * home screen / Mac Chrome app) can keep a stale HTML shell pointing at old JS
 * chunks after a redeploy — the screen breaks when it fetches a chunk that no
 * longer exists. This compares the deployed version (`/api/version`) against
 * the `baseline` the server rendered into THIS page (codex 주요 2 — the shell
 * knows which build it is; no fragile client-remembered first fetch) and
 * refreshes when they differ, but never mid-conversation.
 *
 * When it checks:
 *   1. once on mount,
 *   2. every time the app returns to the foreground (`visibilitychange` →
 *      visible) — the key path for a re-opened iOS standalone PWA, and
 *   3. on a 5-minute poll while open.
 * Concurrent triggers collapse into one in-flight check (codex 보통 4).
 *
 * When it reloads: only when a SUCCESSFUL fetch confirms a different version
 * AND no session is active ({@link isSessionActive}). During a live session the
 * update is simply re-observed by the next check after it ends — a conversation
 * is never cut off. Offline/failed fetches never reload (codex 주요 1), and a
 * rollback to the loaded build is inherently a no-op (codex 보통 3) — all policy
 * lives in the pure {@link decideVersionCheck}. Renders nothing.
 */
import { useEffect } from "react";
import { isSessionActive } from "@/lib/pwa/session-activity";
import {
  decideVersionCheck,
  createSingleFlight,
} from "@/lib/pwa/update-check";

const POLL_INTERVAL_MS = 5 * 60 * 1000;

async function fetchVersion(signal: AbortSignal): Promise<string | null> {
  try {
    const res = await fetch("/api/version", { cache: "no-store", signal });
    if (!res.ok) return null;
    const data: unknown = await res.json();
    const version = (data as { version?: unknown })?.version;
    return typeof version === "string" ? version : null;
  } catch {
    // Offline / aborted / bad response — harmless, ignore.
    return null;
  }
}

export function VersionAutoRefresh({ baseline }: { baseline: string }) {
  useEffect(() => {
    const controller = new AbortController();

    // Single-flight (codex 보통 4): mount + visibility + interval may fire
    // together; only one fetch-and-decide runs at a time, the rest skip.
    const check = createSingleFlight(async () => {
      const fetchedVersion = await fetchVersion(controller.signal);
      // After unmount the abort makes fetchedVersion null → "none" (no reload).
      const effect = decideVersionCheck({
        baseline,
        fetchedVersion,
        sessionActive: isSessionActive(),
      });
      if (effect === "reload") window.location.reload();
    });

    // 1) Check on mount (a shell restored from cache may already be stale).
    void check();

    // 2) Foreground return (core path for a re-opened iOS standalone PWA).
    const onVisibility = () => {
      if (document.visibilityState === "visible") void check();
    };
    document.addEventListener("visibilitychange", onVisibility);

    // 3) Periodic poll while open.
    const interval = window.setInterval(() => void check(), POLL_INTERVAL_MS);

    return () => {
      controller.abort();
      document.removeEventListener("visibilitychange", onVisibility);
      window.clearInterval(interval);
    };
  }, [baseline]);

  return null;
}
